// src/pages/common/UploadDesignModal.js
import React, { useEffect, useRef, useState } from "react";

import Swal from "sweetalert2";
import { uploadDesign } from "../../api/user-service";
import axios from "axios";
import { settings } from "../../helpers/settings";
import { parse } from "exifr";
import extractChunks from "png-chunks-extract";

const UploadDesignModal = ({
  isOpen,
  onClose,
  order,
  item,
  itemSku,
  copyMeta,
}) => {
  const [selectedAreas, setSelectedAreas] = useState({
    front: null,
    back: null,
    pocket: null,
  });

  const [uploading, setUploading] = useState(false);
  const [dotCount, setDotCount] = useState(0);
  const [mockupSize, setMockupSize] = useState({ width: 1, height: 1 });
  const [mockupImage, setMockupImage] = useState(null);
  const [mockupAreas, setMockupAreas] = useState([]);
  const [mockupLoading, setMockupLoading] = useState(false);

  // 🆕 Custom/Personalized ürün kontrolü
  const isCustomOrPersonalizedProduct = (item) => {
    if (!item) return false;

    const itemName = (item.name || "").toLowerCase();
    const itemSku = (item.sku || "").toLowerCase();

    const customRegex = /custom(ize|ized|ization|isation)?/i;
    const personalizedRegex = /personal(ize|ized|ization|isation)?/i;

    return (
      customRegex.test(itemName) ||
      customRegex.test(itemSku) ||
      personalizedRegex.test(itemName) ||
      personalizedRegex.test(itemSku)
    );
  };

  const isCustomProduct = isCustomOrPersonalizedProduct(item);

  // 🆕 Yeni state'ler
  const [saveDesignForLater, setSaveDesignForLater] = useState(
    !isCustomProduct
  );
  const [setDefaultDesign, setSetDefaultDesign] = useState(false);
  const [loadingExistingDesigns, setLoadingExistingDesigns] = useState(false);

  // Debug için: Modal açıldığında item bilgilerini logla
  useEffect(() => {
    if (isOpen && item) {
      console.log("🎯 MODAL OPENED WITH ITEM:", {
        itemName: item.name,
        options: item.options,
        itemSku: itemSku,
        detectedProductType: detectProductType(item),
        detectedColor: detectColor(item),
        detectedSize: detectSize(item),
      });
    }
  }, [isOpen, item]);

  // 🆕 Modal açılınca/kapanınca veya item değişince state'leri sıfırla
  useEffect(() => {
    if (isOpen && item) {
      setSelectedAreas({ front: null, back: null, pocket: null });
      setSetDefaultDesign(false);
      const isCustom = isCustomOrPersonalizedProduct(item);
      setSaveDesignForLater(!isCustom);
    } else if (!isOpen) {
      setSelectedAreas({ front: null, back: null, pocket: null });
      setSetDefaultDesign(false);
    }
  }, [isOpen, item?.lineItemKey, item?.orderItemId, item?.name]);

  // 🔧 Blob URL cleanup
  useEffect(() => {
    const urls = Object.values(selectedAreas)
      .filter((file) => file instanceof File)
      .map((file) => URL.createObjectURL(file));

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedAreas]);

  // Mockup data fetch - GELİŞTİRİLMİŞ VERSİYON
  useEffect(() => {
    const fetchMockupData = async () => {
      if (!item || !isOpen) {
        console.log("❌ Mockup fetch: No item or modal not open");
        return;
      }

      setMockupLoading(true);

      try {
        const productType = detectProductType(item);
        const color = detectColor(item);

        console.log("🔍 Mockup POST Request:", {
          productType,
          color,
          itemName: item.name,
          itemOptions: item.options,
        });

        const res = await axios.post(
          `${settings.apiURL}/get_mockup_by_product.php`,
          {
            product_type: productType,
            color: color,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
            },
            timeout: 10000, // 10 second timeout
          }
        );

        console.log("📦 Mockup API Response:", res.data);

        if (res.data.success && res.data.mockup) {
          const imageUrl = `${settings.apiURL}/${res.data.mockup.image_path}`;
          console.log("✅ Mockup image URL:", imageUrl);

          // Image pre-loading
          const img = new Image();
          img.onload = () => {
            setMockupImage(imageUrl);
            const parsed = JSON.parse(res.data.mockup.positions || "{}");
            const newAreas = Object.entries(parsed).map(([name, pos]) => ({
              name,
              ...pos,
            }));
            setMockupAreas(newAreas);
            console.log("✅ Mockup loaded successfully, areas:", newAreas);
          };
          img.onerror = () => {
            console.error("❌ Mockup image failed to load:", imageUrl);
            setMockupImage(null);
            setMockupAreas([]);
          };
          img.src = imageUrl;
        } else {
          console.warn("⚠️ No matching mockup found:", res.data.message);
          setMockupImage(null);
          setMockupAreas([]);

          // Kullanıcıya bilgi ver
          Swal.fire({
            title: "Mockup Not Found",
            text: `No mockup found for ${productType} in ${color}. Using default layout.`,
            icon: "info",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      } catch (err) {
        console.error("❌ Mockup fetch failed:", err);
        setMockupImage(null);
        setMockupAreas([]);

        Swal.fire({
          title: "Error Loading Mockup",
          text: "Failed to load product mockup. Please try again.",
          icon: "error",
          timer: 3000,
          showConfirmButton: false,
        });
      } finally {
        setMockupLoading(false);
      }
    };

    if (isOpen && item) {
      fetchMockupData();
    } else {
      // Modal kapandığında reset
      setMockupImage(null);
      setMockupAreas([]);
    }
  }, [item, isOpen]);

  // 🆕 Mevcut tasarımları getirme fonksiyonu
  const fetchExistingDesigns = async (sku) => {
    if (!sku) return;

    setLoadingExistingDesigns(true);

    const encodedSku = encodeURIComponent(sku);
    console.log("🔍 Fetching designs for SKU:", sku, "| Encoded:", encodedSku);

    try {
      const res = await axios.get(
        `${settings.apiURL}/get_ready_to_press_designs.php?sku=${encodedSku}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
          },
        }
      );

      console.log("📦 Response from server:", res.data);
      console.log("📦 Designs found:", res.data.designs?.length || 0);

      if (res.data.success) {
        if (!res.data.designs || res.data.designs.length === 0) {
          setSetDefaultDesign(false);
          setLoadingExistingDesigns(false);

          Swal.fire({
            title: "No Designs Found",
            text: 'No previous designs for this SKU. Use "Save Design For Later" to create defaults.',
            icon: "info",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
            position: "center",
            toast: false,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#ffffff",
            iconColor: "#ffffff",
          });
          return;
        }

        const newSelectedAreas = { front: null, back: null, pocket: null };
        let defaultDesignsFound = 0;

        for (const design of res.data.designs) {
          try {
            if (design.image_base64) {
              const response = await fetch(design.image_base64);
              const blob = await response.blob();
              const file = new File([blob], `${sku}-${design.region}.png`, {
                type: "image/png",
              });
              newSelectedAreas[design.region.toLowerCase()] = file;
              defaultDesignsFound++;
              console.log(
                `✅ Loaded design for SKU: ${sku}, Region: ${design.region}`
              );
            } else {
              console.warn(
                `❌ No image data for SKU: ${sku}, Region: ${design.region}`
              );
            }
          } catch (err) {
            console.warn("Could not load existing design:", err);
          }
        }

        setSelectedAreas(newSelectedAreas);

        if (defaultDesignsFound > 0) {
          Swal.fire({
            title: "Designs Loaded Successfully",
            text: `${defaultDesignsFound} default design(s) loaded for SKU: ${sku}`,
            icon: "success",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
            position: "center",
            toast: false,
            background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
            color: "#ffffff",
            iconColor: "#ffffff",
          });
        }
      }
    } catch (err) {
      console.error("Error fetching existing designs:", err);
      setSetDefaultDesign(false);
    } finally {
      setLoadingExistingDesigns(false);
    }
  };

  // 🆕 SetDefaultDesign checkbox handler
  const handleSetDefaultDesign = (e) => {
    const checked = e.target.checked;

    if (checked && !itemSku) {
      Swal.fire({
        title: "SKU Not Found",
        text: "Unable to load default designs. SKU information is missing for this item.",
        icon: "warning",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        position: "center",
        toast: false,
        background: "linear-gradient(135deg, #ff9800 0%, #ff5722 100%)",
        color: "#ffffff",
        iconColor: "#ffffff",
      });
      return;
    }

    setSetDefaultDesign(checked);

    if (checked && itemSku) {
      fetchExistingDesigns(itemSku);
    } else if (!checked) {
      setSelectedAreas({
        front: null,
        back: null,
        pocket: null,
      });
    }
  };

  // DPI kontrol fonksiyonları
  const getPNGDPIDetailed = (arrayBuffer) => {
    try {
      const chunks = extractChunks(new Uint8Array(arrayBuffer));
      const results = [];

      for (const chunk of chunks) {
        if (chunk.name === "pHYs") {
          const xPixelsPerUnit =
            (chunk.data[0] << 24) |
            (chunk.data[1] << 16) |
            (chunk.data[2] << 8) |
            chunk.data[3];
          const yPixelsPerUnit =
            (chunk.data[4] << 24) |
            (chunk.data[5] << 16) |
            (chunk.data[6] << 8) |
            chunk.data[7];
          const unit = chunk.data[8];

          if (unit === 1) {
            const dpiX = Math.round(xPixelsPerUnit * 0.0254);
            const dpiY = Math.round(yPixelsPerUnit * 0.0254);
            results.push({
              dpiX,
              dpiY,
              source: "pHYs chunk",
              confidence: "high",
            });
          }
        }

        if (chunk.name === "eXIf") {
          try {
            const exifData = parse(chunk.data.buffer);
            if (exifData && exifData.XResolution && exifData.YResolution) {
              const xRes = exifData.XResolution;
              const yRes = exifData.YResolution;
              const resUnit = exifData.ResolutionUnit;

              let dpiX, dpiY;
              if (resUnit === 2) {
                dpiX = Math.round(xRes);
                dpiY = Math.round(yRes);
              } else if (resUnit === 3) {
                dpiX = Math.round(xRes * 2.54);
                dpiY = Math.round(yRes * 2.54);
              }

              if (dpiX && dpiY) {
                results.push({
                  dpiX,
                  dpiY,
                  source: "eXIf chunk",
                  confidence: "high",
                });
              }
            }
          } catch (e) {
            console.log("Error parsing eXIf chunk:", e);
          }
        }

        if (chunk.name === "iTXt") {
          try {
            const text = new TextDecoder().decode(chunk.data);
            const dpiMatch = text.match(/dpi[:\s]*(\d+)/i);
            if (dpiMatch) {
              const dpi = parseInt(dpiMatch[1]);
              results.push({
                dpiX: dpi,
                dpiY: dpi,
                source: "iTXt chunk",
                confidence: "medium",
              });
            }
          } catch (e) {
            console.log("Error parsing iTXt chunk:", e);
          }
        }
      }

      return results;
    } catch (error) {
      console.log("Error reading PNG chunks:", error);
      return [];
    }
  };

  const getPNGDPIBinary = (arrayBuffer) => {
    try {
      const view = new DataView(arrayBuffer);
      let offset = 8;

      while (offset < view.byteLength - 12) {
        const length = view.getUint32(offset);
        const type = new TextDecoder().decode(
          new Uint8Array(arrayBuffer, offset + 4, 4)
        );

        if (type === "pHYs") {
          const xPixelsPerUnit = view.getUint32(offset + 8);
          const yPixelsPerUnit = view.getUint32(offset + 12);
          const unit = view.getUint8(offset + 16);

          if (unit === 1) {
            const dpiX = Math.round(xPixelsPerUnit * 0.0254);
            const dpiY = Math.round(yPixelsPerUnit * 0.0254);
            return { dpiX, dpiY, source: "binary pHYs", confidence: "high" };
          }
        }

        offset += 12 + length;
      }

      return null;
    } catch (error) {
      console.log("Error in binary analysis:", error);
      return null;
    }
  };

  const checkImageDPI = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const results = [];

      const chunkResults = getPNGDPIDetailed(arrayBuffer);
      results.push(...chunkResults);

      const binaryResult = getPNGDPIBinary(arrayBuffer);
      if (binaryResult) {
        results.push(binaryResult);
      }

      try {
        const exifData = await parse(arrayBuffer);
        if (exifData && exifData.XResolution && exifData.YResolution) {
          const xRes = exifData.XResolution;
          const yRes = exifData.YResolution;
          const resUnit = exifData.ResolutionUnit;

          let dpiX, dpiY;
          if (resUnit === 2) {
            dpiX = Math.round(xRes);
            dpiY = Math.round(yRes);
          } else if (resUnit === 3) {
            dpiX = Math.round(xRes * 2.54);
            dpiY = Math.round(yRes * 2.54);
          }

          if (dpiX && dpiY) {
            results.push({ dpiX, dpiY, source: "EXIF", confidence: "medium" });
          }
        }
      } catch (e) {
        console.log("EXIF parsing failed:", e);
      }

      if (results.length > 0) {
        const highConfidence = results.filter((r) => r.confidence === "high");
        if (highConfidence.length > 0) {
          console.log("DPI found (high confidence):", highConfidence[0]);
          return highConfidence[0];
        }
        console.log("DPI found (medium confidence):", results[0]);
        return results[0];
      }

      console.log("No DPI information found");
      return { dpiX: null, dpiY: null, source: "none", confidence: "none" };
    } catch (error) {
      console.log("Comprehensive DPI check failed:", error);
      return { dpiX: null, dpiY: null, source: "error", confidence: "none" };
    }
  };

  const handleClickArea = async (area) => {
    const lower = area.toLowerCase();
    console.log("🖱️ Clicked area:", area);

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) {
        console.log("❌ No file selected");
        return;
      }

      console.log("📁 File selected:", file.name, "size:", file.size);

      const dpiInfo = await checkImageDPI(file);

      if (dpiInfo.dpiX === null || dpiInfo.dpiY === null) {
        const regionNames = {
          front: "FRONT",
          back: "BACK",
          pocket: "POCKET",
          leftarm: "LEFTARM",
          rightarm: "RIGHTARM",
        };

        const regionName = regionNames[lower] || lower.toUpperCase();

        Swal.fire({
          title: "DPI Information Not Found",
          html: `
      <div style="text-align: left;">
        <p><strong>${regionName}</strong> area design does not contain DPI information!</p>
        <p>Please save your design with proper DPI settings and upload again.</p>
        <br>
        <p><strong>Note:</strong> DPI information could not be found in image metadata.</p>
      </div>
    `,
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#ff6600",
        });

        return;
      }

      if (dpiInfo.dpiX < 96 || dpiInfo.dpiY < 96) {
        const regionNames = {
          front: "FRONT",
          back: "BACK",
          pocket: "POCKET",
          leftarm: "LEFTARM",
          rightarm: "RIGHTARM",
        };

        const regionName = regionNames[lower] || lower.toUpperCase();

        Swal.fire({
          title: "DPI Warning",
          html: `
      <div style="text-align: left;">
        <p><strong>${regionName}</strong> area design has a DPI value that is too low!</p>
        <p><strong>Current DPI:</strong> ${dpiInfo.dpiX} x ${dpiInfo.dpiY}</p>
        <p><strong>Minimum Required DPI:</strong> 96</p>
        <p><strong>Source:</strong> ${dpiInfo.source}</p>
        <br>
        <p>Please save your design with at least 96 DPI and upload again.</p>
      </div>
    `,
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#ff6600",
        });

        return;
      }

      console.log("✅ DPI check passed, adding file to area:", area);
      setSelectedAreas((prev) => {
        const updated = { ...prev };

        if (lower === "front") {
          updated.front = file;
          updated.pocket = null;
        } else if (lower === "pocket") {
          updated.pocket = file;
          updated.front = null;
        } else {
          updated[lower] = file;
        }

        console.log("🔄 Updated selected areas:", updated);
        return updated;
      });
    };

    input.click();
  };

  const normalizeAreaName = (name) => {
    const n = name.toLowerCase().replace(/\s+/g, "").trim();

    if (n.includes("fullfront") || n === "front" || n.includes("front"))
      return "front";
    if (n.includes("pocket")) return "pocket";
    if (n.includes("back")) return "back";
    return n;
  };

  const normalizeText = (text) => {
    if (!text) return "";

    return text
      .toString()
      .toLocaleLowerCase("en-US")
      .replace(/ı/g, "i")
      .replace(/İ/g, "i")
      .replace(/ğ/g, "g")
      .replace(/Ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/Ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/Ş/g, "s")
      .replace(/ç/g, "c")
      .replace(/Ç/g, "c")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  };

  const getSizeValue = (options) => {
    if (!Array.isArray(options)) return "";

    const sizeOption = options.find((opt) => {
      const name = normalizeText(opt.name);
      return (
        name.includes("size") || name.includes("sıze") || name.includes("beden")
      );
    });

    if (sizeOption?.value) {
      const parts = sizeOption.value.split(" - ");
      if (parts.length > 1) {
        return parts[0];
      }
      return sizeOption.value;
    }

    return "";
  };

  // GELİŞTİRİLMİŞ detectProductType
  const detectProductType = (item) => {
    if (!item?.name) {
      console.log("❌ detectProductType: No item or name");
      return "Tshirt";
    }

    const name = (item.name || "").toLowerCase();
    const options = item.options || [];

    const sizeOption = options.find(
      (opt) =>
        normalizeText(opt.name).includes("size") ||
        normalizeText(opt.name).includes("beden")
    );

    const colorOption = options.find(
      (opt) =>
        normalizeText(opt.name).includes("color") ||
        normalizeText(opt.name).includes("colour") ||
        normalizeText(opt.name).includes("renk")
    );

    const sizeValue = (sizeOption?.value || "").toLowerCase();
    const colorValue = (colorOption?.value || "").toLowerCase();

    const combinedText = `${name} ${sizeValue} ${colorValue}`;

    console.log("🔍 Product Detection Debug:", {
      name,
      sizeValue,
      colorValue,
      combinedText,
    });

    const productPatterns = [
      { pattern: /racerback|tank.*top/i, type: "Racerback" },
      { pattern: /crop.*tank|crop.*top/i, type: "Crop" },
      { pattern: /v.*neck|v-neck/i, type: "V-neck" },
      { pattern: /hoodie|hoody/i, type: "Hoodie" },
      { pattern: /sweatshirt|sweat.*shirt/i, type: "Sweatshirt" },
      { pattern: /polo|polo.*shirt/i, type: "Polo" },
      { pattern: /long.*sleeve|long.*sleeved/i, type: "Long Sleeve" },
      { pattern: /short.*sleeve|short.*sleeved/i, type: "Tshirt" },
      { pattern: /t.*shirt|tshirt|tee|shirt/i, type: "Tshirt" },
    ];

    // Önce size alanında ara
    if (sizeValue) {
      console.log("🔎 Searching in size field:", sizeValue);
      for (const { pattern, type } of productPatterns) {
        if (pattern.test(sizeValue)) {
          console.log("✅ Matched from size field:", type, "in:", sizeValue);
          return type;
        }
      }

      // Basit eşleşmeler
      if (sizeValue.includes("hoodie")) {
        console.log("✅ Simple match: hoodie in size field");
        return "Hoodie";
      }
      if (sizeValue.includes("sweatshirt")) {
        console.log("✅ Simple match: sweatshirt in size field");
        return "Sweatshirt";
      }
      if (sizeValue.includes("polo")) {
        console.log("✅ Simple match: polo in size field");
        return "Polo";
      }
      if (sizeValue.includes("long")) {
        console.log("✅ Simple match: long in size field");
        return "Long Sleeve";
      }
      if (sizeValue.includes("tee") || sizeValue.includes("t-shirt")) {
        console.log("✅ Simple match: tshirt in size field");
        return "Tshirt";
      }
    }

    // Sonra combined text'te ara
    console.log("🔎 Searching in combined text:", combinedText);
    for (const { pattern, type } of productPatterns) {
      if (pattern.test(combinedText)) {
        console.log(
          "✅ Matched from combined text:",
          type,
          "in:",
          combinedText
        );
        return type;
      }
    }

    console.log("⚠️ No specific product type matched, defaulting to Tshirt");
    return "Tshirt";
  };

  const detectSize = (item) => {
    const opt = item?.options || [];
    const rawFull = getSizeValue(opt) || "MEDIUM";

    const extractSizePart = (value) => {
      const parts = value.split("-");
      return parts[parts.length - 1].trim().toUpperCase();
    };

    const raw = extractSizePart(rawFull);

    const validSizes = [
      "XSMALL",
      "SMALL",
      "MEDIUM",
      "LARGE",
      "XLARGE",
      "2XL",
      "3XL",
      "S",
      "M",
      "L",
      "XL",
      "2X",
      "3X",
    ];

    const normalizeSize = (val) => {
      const map = {
        S: "SMALL",
        M: "MEDIUM",
        L: "LARGE",
        XL: "XLARGE",
        "2X": "2XL",
        "3X": "3XL",
      };
      return map[val] || val;
    };

    const match = validSizes.find((size) => raw === size);
    const result = match ? normalizeSize(match) : "MEDIUM";
    console.log("📏 Detected size:", result, "from raw:", raw);
    return result;
  };

  const detectColor = (item) => {
    if (!item?.options) {
      console.log("❌ detectColor: No options, returning white");
      return "white";
    }

    const options = item.options;

    const colorOption = options.find((opt) => {
      const optName = normalizeText(opt.name);
      return (
        optName.includes("color") ||
        optName.includes("colour") ||
        optName.includes("renk") ||
        optName.includes("colorway") ||
        optName.includes("couleur")
      );
    });

    let rawColor = (colorOption?.value || "white").toLowerCase().trim();

    console.log("🎨 Color Detection - Raw:", rawColor);

    if (
      !rawColor ||
      rawColor === "" ||
      rawColor === "not requested on this item"
    ) {
      console.log("🎨 No color found, returning white");
      return "white";
    }

    // ÖNEMLİ: gray/grey düzeltmesi - tüm gray'leri grey yap
    let normalizedInput = rawColor
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/gray/g, "grey"); // gray -> grey dönüşümü

    console.log("🎨 Normalized color input (grey converted):", normalizedInput);

    // Database mapping - database'deki GERÇEK renk isimlerine göre
    const colorMap = {
      // Database'de "dark gray heather" var, o yüzden grey değil gray kullan
      dark_grey_heather: "dark_gray_heather", // grey -> gray
      dark_gray_heather: "dark_gray_heather",

      dark_grey: "dark_gray", // grey -> gray
      dark_gray: "dark_gray",

      grey_heather: "sport_athletic_heather", // grey -> athletic heather
      gray_heather: "sport_athletic_heather",
      sport_athletic_heather: "sport_athletic_heather",
      sport_grey_heather: "sport_athletic_heather",
      sport_gray_heather: "sport_athletic_heather",

      // Diğer renkler
      heather_deep_teal: "heather_deep_teal",
      heather_grass_green: "heather_grass_green",
      heather_maroon: "heather_maroon",
      heather_mauve: "heather_mauve",
      heather_midnigh_navy: "heather_midnigh_navy",
      heather_military_green: "heather_military_green",
      forest_green: "forest_green",
      kelly_green: "kelly_green",
      military_green: "military_green",
      millitary_green: "millitary_green",
      light_pink: "light_pink",
      light_green: "light_green",
      dark_chocolate: "dark_chocolate",
      true_royal: "true_royal",
      blue_jean: "blue_jean",
      blue_spruce: "blue_spruce",
      red: "red",
      black: "black",
      white: "white",
      navy: "navy",
    };

    // Önce tam eşleşme
    if (colorMap[normalizedInput]) {
      console.log("✅ Color exact match:", colorMap[normalizedInput]);
      return colorMap[normalizedInput];
    }

    // Kısmi eşleşme
    for (const [key, value] of Object.entries(colorMap)) {
      if (normalizedInput.includes(key)) {
        console.log("✅ Color partial match:", value, "for:", normalizedInput);
        return value;
      }
    }

    // Gray/grey düzeltmesi olmadan tekrar dene
    const nonGreyNormalized = rawColor
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_");

    console.log("⚠️ Color using non-grey normalized:", nonGreyNormalized);
    return nonGreyNormalized;
  };

  const handleRemoveArea = (area) => {
    console.log("🗑️ Removing area:", area);
    setSelectedAreas((prev) => ({
      ...prev,
      [area]: null,
    }));
  };

  const getHexByColorName = (colorName) => {
    if (!colorName) return "#cccccc";

    const normalize = (str) =>
      str
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");

    const colorMap = {
      blossom: "#ffb7ce",
      blue_jean: "#5d8aa8",
      blue_spruce: "#486b60",
      brick: "#b22222",
      chambray: "#91a3b0",
      espresso: "#4b3832",
      gray: "#808080",
      ivory: "#f6e27f",
      light_green: "#90ee90",
      moss: "#8a9a5b",
      mustard: "#ffdb58",
      orchid: "#da70d6",
      pepper: "#4b3621",
      red: "#ff0000",
      sage: "#bcb88a",
      sandstone: "#c2b280",
      terracotta: "#e2725b",
      violet: "#8f00ff",
      watermelon: "#fc6c85",
      white: "#ffffff",
      yam: "#ff8c00",
      black: "#000000",
      dark_chocolate: "#5c4033",
      dark_grey: "#2e2e2e",
      dark_grey_heather: "#4b4b4b", // Burada dark_grey_heather var
      forest_green: "#228b22",
      heather_deep_teal: "#006d77",
      heather_grass_green: "#7bb661",
      heather_maroon: "#800000",
      heather_mauve: "#b784a7",
      heather_midnigh_navy: "#191970",
      heather_military_green: "#4b5320",
      kelly_green: "#4cbb17",
      light_pink: "#ffb6c1",
      maroon: "#800000",
      millitary_green: "#4b5320",
      natural: "#f5f5dc",
      navy: "#000080",
      pink: "#ffc0cb",
      raspberry: "#e30b5c",
      sand: "#f5deb3",
      sport_athletic_heather: "#cfcfcf",
      true_royal: "#274b8f",
    };

    const normalizedKey = normalize(colorName);
    const result = colorMap[normalizedKey] || "#cccccc";
    console.log(
      "🎨 Color to HEX:",
      colorName,
      "->",
      normalizedKey,
      "->",
      result
    );
    return result;
  };

  const handleSendDesigns = async () => {
    console.log("🚀 Sending designs...");
    const hasAtLeastOneDesign = Object.values(selectedAreas).some(
      (file) => file
    );

    console.log("📊 Selected areas:", selectedAreas);
    console.log("✅ Has designs:", hasAtLeastOneDesign);

    if (!hasAtLeastOneDesign) {
      Swal.fire(
        "Warning",
        "Please select at least one design to upload.",
        "warning"
      );
      setUploading(false);
      return;
    }

    if (!order || !order.items) {
      console.log("❌ No order or items");
      return;
    }

    setUploading(true);
    const orderNumber =
      "#" + (order.orderNumber?.replace("#", "") || "UnknownOrder");

    try {
      const printableItems = order.items.filter(
        (itm) =>
          itm.name &&
          !itm.name.toLowerCase().includes("discount") &&
          !itm.name.toLowerCase().includes("shipping")
      );

      const size = detectSize(item);
      const color = detectColor(item);
      const quantity = item?.quantity || 1;

      const getShortProductName = () => {
        const sizeOption = item?.options?.find((opt) =>
          normalizeText(opt.name).includes("size")
        );
        return sizeOption?.value?.split(" - ")?.[0] || "Product";
      };

      const productName = getShortProductName();
      const shopName = copyMeta?.shopName || "Shop";
      const customerName = copyMeta?.customerName || "Customer";
      const userName = copyMeta?.userName || "User";
      const orderItemId = (item?.orderItemId || "123456").toString().trim();
      const lineItemKey = (item?.lineItemKey || "UNKNOWN").toString().trim();
      const uploadPromises = [];

      console.log("📦 Upload details:", {
        orderNumber,
        productName,
        size,
        color,
        quantity,
        saveDesignForLater,
      });

      for (let area of Object.keys(selectedAreas)) {
        const file = selectedAreas[area];
        if (!file) continue;

        const filename =
          `${lineItemKey} - ${shopName} - ${orderNumber} - ${customerName} - ${userName} - ` +
          `${productName} - ${size} - ${color} - quantity: ${quantity} - ${area.toUpperCase()}.png`;

        const formData = new FormData();
        formData.append("design", file, filename);
        formData.append("orderNumber", orderNumber.replace("#", ""));
        formData.append("lineItemKey", item.lineItemKey);
        formData.append("size", size);
        formData.append("orderItemId", orderItemId);
        formData.append("saveForLater", saveDesignForLater ? "true" : "false");
        formData.append("sku", itemSku || "");
        formData.append("region", area);

        console.log(`📤 Uploading ${area} area:`, filename);

        uploadPromises.push(
          uploadDesign(formData).then((res) => {
            if (!res || res.success === false) {
              console.error("Upload failed response:", res);
              throw new Error(res?.message || `Upload failed for ${filename}`);
            }
            console.log(`✅ ${area} area uploaded successfully`);
          })
        );
      }

      await Promise.all(uploadPromises);
      console.log("🎉 All designs uploaded successfully!");
      Swal.fire("Success", "Designs uploaded successfully!", "success");
      onClose();
    } catch (err) {
      console.error("❌ Upload error:", err);
      Swal.fire("Error", err.message || "Failed to upload designs.", "error");
    }

    setUploading(false);
  };

  useEffect(() => {
    if (uploading) {
      const interval = setInterval(() => {
        setDotCount((prev) => (prev + 1) % 4);
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDotCount(0);
    }
  }, [uploading]);

  const productType = isOpen && item ? detectProductType(item) : "";
  const size = isOpen && item ? detectSize(item) : "";
  const color = isOpen && item ? detectColor(item) : "white";

  console.log("🎯 FINAL DETECTION RESULTS:", {
    productType,
    size,
    color,
    mockupImage: !!mockupImage,
    mockupAreas: mockupAreas.length,
    mockupLoading,
  });

  if (!isOpen || !order) {
    console.log("❌ Modal not open or no order");
    return null;
  }

  return (
    <div style={styles.backdrop}>
      <div
        style={{
          ...styles.modal,
          maxWidth: 820,
          width: "95%",
          height: "auto",
          padding: 16,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <button onClick={onClose} style={styles.close}>
          ×
        </button>
        <h3 style={{ marginBottom: 10, textAlign: "center" }}>
          Upload Designs
        </h3>

        {mockupLoading && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <p>Loading mockup...</p>
          </div>
        )}

        {!mockupLoading && !mockupImage && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <p>No mockup available for this product.</p>
            <p>
              Product: {productType}, Color: {color}
            </p>
          </div>
        )}

        {mockupImage && (
          <div
            style={{
              position: "relative",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div style={{ position: "relative", width: "fit-content" }}>
              {/* Renk katmanı */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: getHexByColorName(color),
                  WebkitMaskImage: `url(${mockupImage})`,
                  WebkitMaskRepeat: "no-repeat",
                  WebkitMaskSize: "contain",
                  WebkitMaskPosition: "center",
                  maskImage: `url(${mockupImage})`,
                  maskRepeat: "no-repeat",
                  maskSize: "contain",
                  maskPosition: "center",
                  mixBlendMode: "multiply",
                  opacity: 1,
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              />

              {/* Görsel */}
              <img
                src={mockupImage}
                alt="mockup"
                onLoad={(e) => {
                  const img = e.target;
                  setMockupSize({
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                  });
                  console.log(
                    "📐 Mockup Size:",
                    img.naturalWidth,
                    img.naturalHeight
                  );
                }}
                style={{
                  width: "512px",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />

              {/* Kutular */}
              <div style={{ position: "absolute", inset: 0 }}>
                {mockupAreas
                  .filter((area) => {
                    const alias = normalizeAreaName(area.name);
                    const hasFrontSelected = !!selectedAreas.front;
                    const hasPocketSelected = !!selectedAreas.pocket;

                    if (hasFrontSelected && alias === "pocket") return false;
                    if (hasPocketSelected && alias === "front") return false;

                    return true;
                  })
                  .map((area) => {
                    const alias = normalizeAreaName(area.name);
                    const selected = selectedAreas[alias] || null;

                    const boxStyle = {
                      position: "absolute",
                      top: `${area.y * mockupSize.height}px`,
                      left: `${area.x * mockupSize.width}px`,
                      width: `${area.width * mockupSize.width}px`,
                      height: `${area.height * mockupSize.height}px`,
                      transform: `rotate(${area.rotate || 0}deg)`,
                      zIndex: 10,
                      cursor: "pointer",
                    };

                    if (!selected) {
                      return (
                        <div
                          key={area.name}
                          onClick={() =>
                            handleClickArea(normalizeAreaName(area.name))
                          }
                          style={{
                            ...boxStyle,
                            border: "2px dashed #aaa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255,255,255,0.4)",
                          }}
                        >
                          <span style={styles.plus}>+</span>
                        </div>
                      );
                    }

                    return (
                      <React.Fragment
                        key={`${area.name}-${item?.lineItemKey}-${selected?.name}`}
                      >
                        <img
                          src={URL.createObjectURL(selected)}
                          alt={area.name}
                          style={{ ...boxStyle, objectFit: "contain" }}
                        />

                        {(() => {
                          const alias = normalizeAreaName(area.name);
                          const offset = alias.includes("arm")
                            ? { top: -15, left: -25 }
                            : { top: -5, left: -15 };

                          const buttonStyle = {
                            position: "absolute",
                            top: `${area.y * mockupSize.height + offset.top}px`,
                            left: `${
                              area.x * mockupSize.width +
                              area.width * mockupSize.width +
                              offset.left
                            }px`,
                            ...styles.removeButton,
                          };

                          return (
                            <button
                              onClick={() => handleRemoveArea(alias)}
                              style={buttonStyle}
                            >
                              ×
                            </button>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* 🆕 Checkbox'lar - sol alt köşe */}
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            left: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            backgroundColor: "rgba(255,255,255,0.9)",
            padding: "10px",
            borderRadius: "6px",
            fontSize: "14px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: isCustomProduct ? "default" : "pointer",
            }}
            onClick={(e) => {
              if (isCustomProduct) {
                e.preventDefault();
                Swal.fire({
                  title: "Custom Product Detected",
                  text: "You cannot load a default design for later use on custom products.",
                  icon: "warning",
                  timer: 4000,
                  timerProgressBar: true,
                  showConfirmButton: false,
                  position: "center",
                  toast: false,
                  background:
                    "linear-gradient(135deg, #ff9800 0%, #ff5722 100%)",
                  color: "#ffffff",
                  iconColor: "#ffffff",
                });
              }
            }}
          >
            <input
              type="checkbox"
              checked={saveDesignForLater}
              onChange={(e) => {
                if (!isCustomProduct) {
                  setSaveDesignForLater(e.target.checked);
                }
              }}
              disabled={isCustomProduct}
              style={{
                cursor: isCustomProduct ? "default" : "pointer",
                pointerEvents: "none",
              }}
            />
            <span style={{ color: isCustomProduct ? "#999" : "#000" }}>
              Save Design For Later
            </span>
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={setDefaultDesign}
              onChange={handleSetDefaultDesign}
              disabled={!itemSku}
            />
            Load Previous Designs
            {loadingExistingDesigns && <span>⏳</span>}
          </label>

          {!itemSku && (
            <small style={{ color: "#888", fontSize: "12px" }}>
              SKU bilgisi bulunamadı
            </small>
          )}
        </div>

        <button
          onClick={handleSendDesigns}
          disabled={uploading}
          style={{
            backgroundColor: "#ff6600",
            color: "#fff",
            padding: "10px 20px",
            border: "none",
            borderRadius: "4px",
            fontWeight: "bold",
            cursor: uploading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "30px auto 0",
          }}
        >
          {uploading ? `Sending${".".repeat(dotCount)}` : "Send Designs"}
        </button>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 999,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "auto",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: 20,
    width: "90%",
    maxWidth: "820px",
    maxHeight: "90vh",
    overflowY: "auto",
    position: "relative",
    boxShadow: "0 0 20px rgba(0,0,0,0.3)",
  },
  close: {
    position: "absolute",
    top: 10,
    right: 15,
    fontSize: 24,
    cursor: "pointer",
    border: "none",
    background: "none",
  },
  plus: {
    fontSize: "32px",
    color: "#888",
    fontWeight: "bold",
  },
  removeButton: {
    position: "absolute",
    background: "#ff4444",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 24,
    height: 24,
    textAlign: "center",
    lineHeight: "24px",
    cursor: "pointer",
    fontSize: 16,
    fontWeight: "bold",
    zIndex: 20,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 0,
  },
};

export default UploadDesignModal;
